import { useQueryClient } from "@tanstack/react-query";

import type { Route } from "./+types/$locale.catalogue._index";
import { getLocations } from "@/api/locations";
import { buildSeoMeta } from "@/components/seo/meta";
import { Catalog } from "@/features/catalog/routes/Catalog";

/**
 * Build-time loader for /:locale/catalogue.
 *
 * Pre-fetches the locations list so the runtime `useLocations` hook (used by
 * the location-picker dropdowns inside `<Catalog>` via `SearchWidget` /
 * `LocationSelectContent`) returns seeded data on first render with no
 * network round-trip.
 *
 * We deliberately do NOT pre-fetch the products list here: `useProducts` keys
 * include user-driven filters (categories, ageRanges, brands, query,
 * isAvailable, sort, page) plus locationId/dates from `useLocationStore` — at
 * build time none of those are known, so any seed would never match the
 * runtime query key. The products list will fetch on the client like before;
 * only the SEO head + locations cache are prerendered.
 */
export async function loader() {
    const locations = await getLocations();
    return { locations };
}

export function meta({ params }: Route.MetaArgs) {
    const locale = (params.locale as string) ?? "en";
    return buildSeoMeta({
        title: "Baby Equipment Catalogue — Rent or Buy Strollers, Cribs & More",
        description:
            "Browse our premium baby equipment rental catalogue. Find strollers, cribs, car seats, and more for rent or purchase. Flexible rental periods and affordable pricing.",
        path: "/catalogue",
        keywords:
            "baby equipment rental, stroller rental, crib rental, car seat rental, baby gear hire, baby equipment for sale",
        breadcrumbs: [
            { name: "Home", path: "/" },
            { name: "Catalogue", path: "/catalogue" },
        ],
        // TODO Phase 6: hydrate availableLocales from root loader manifest
        locale,
        availableLocales: [{ code: "en" }],
    });
}

export default function CatalogueIndexRoute({ loaderData }: Route.ComponentProps) {
    const queryClient = useQueryClient();
    // Seed `useLocations` (key shape from app/hooks/useLocations.ts).
    queryClient.setQueryData(["locations"], loaderData.locations);
    return <Catalog />;
}
